import React, { useEffect, useState } from "react";
import { Card, ListGroup, Row, Col, Button } from "react-bootstrap";
import { collection, getDocs, orderBy, query, doc, updateDoc } from "@firebase/firestore";
import { db } from "../../../firebase/FirebaseProvider";
import { Location } from "../../types/Types";
import { Link } from "react-router-dom";
import PageTracking from "../../auth/PageTracking";
import SuperAdminNav from "./SuperAdminNav";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHammer, faPlane, faHotel, faUmbrellaBeach, faGraduationCap, faTimes, faPencilAlt, faTrain } from '@fortawesome/free-solid-svg-icons';
import { getCountryFlag } from "../../util/Util";

function SuperAdminPubs() {
    const [locations, setLocations] = useState<Location[]>([]);
    const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [filter, setFilter] = useState<string>('NO_COUNTRY');

    useEffect(() => {
        (async () => {
            await loadLocations();
        })();
    }, []);

    useEffect(() => {
        applyFilter();
    }, [locations, filter]);

    const applyFilter = () => {
        if (filter === 'NO_COUNTRY') {
            setFilteredLocations(locations.filter(loc => !loc.country || loc.country === ''));
        } else {
            setFilteredLocations(locations);
        }
    };

    const setCountry = async (locationId: string, countryCode: string) => {
        try {
            const locationRef = doc(db, 'locations', locationId);
            await updateDoc(locationRef, {
                country: countryCode
            });
            
            // Update local state
            setLocations(prev => prev.map(loc => 
                loc.id === locationId ? { ...loc, country: countryCode } : loc
            ));
        } catch (error) {
            console.error("Error updating country:", error);
            alert("Failed to update country");
        }
    };

    const loadLocations = async () => {
        try {
            setLoading(true);
            
            const locationsQuery = query(
                collection(db, 'locations'),
                orderBy('name', 'asc')
            );

            const querySnapshot = await getDocs(locationsQuery);
            const locs: Location[] = [];
            
            querySnapshot.forEach((doc) => {
                const locationData = doc.data() as Location;
                locs.push({...locationData, id: doc.id});
            });

            console.log(`Loaded locations: ${locs.length} documents`);
            setLocations(locs);
        } catch (error) {
            console.error("Error loading locations:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <PageTracking />
            <SuperAdminNav />
            
            <Card className={"mb-3"}>
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <div><b>All Pubs</b></div>
                            {!loading && (
                                <small className="text-muted">Total: {filteredLocations.length} pubs</small>
                            )}
                        </div>
                        {!loading && (
                            <div>
                                <Button 
                                    variant={filter === 'ALL' ? "primary" : "outline-primary"}
                                    size="sm"
                                    className="mr-2"
                                    onClick={() => setFilter('ALL')}
                                >
                                    All
                                </Button>
                                <Button 
                                    variant={filter === 'NO_COUNTRY' ? "primary" : "outline-primary"}
                                    size="sm"
                                    onClick={() => setFilter('NO_COUNTRY')}
                                >
                                    No Country
                                </Button>
                            </div>
                        )}
                    </div>
                </Card.Header>
                <Card.Body style={{ padding: 0 }}>
                    {loading ? (
                        <div className="text-center p-3">Loading...</div>
                    ) : filteredLocations.length === 0 ? (
                        <div className="text-center p-3">No pubs found</div>
                    ) : (
                        <ListGroup variant="flush">
                            {filteredLocations.map((location) => (
                                <ListGroup.Item key={location.id}>
                                    <Row className="align-middle flex-nowrap">
                                        <Col className="d-flex flex-column align-items-start justify-content-center text-truncate">
                                            <div>
                                                <Link to={`/location/${location.id}/comments`} className={"p-0"}>
                                                    <div className={"location-tag"}>{location.name}</div>
                                                </Link>
                                                {location.airport && <small className="text-truncate">
                                                    <FontAwesomeIcon className="ml-1 animated zoomIn icon-plane" icon={faPlane} />
                                                </small>}
                                                {location.hotel && <small className="text-truncate">
                                                    <FontAwesomeIcon className="ml-1 animated zoomIn icon-hotel" icon={faHotel} />
                                                </small>}
                                                {location.holidayPark && <small className="text-truncate">
                                                    <FontAwesomeIcon className="ml-1 animated zoomIn icon-holidaypark" icon={faUmbrellaBeach} />
                                                </small>}
                                                {location.isUniversity && <small className="text-truncate">
                                                    <FontAwesomeIcon className="ml-1 animated zoomIn icon-university" icon={faGraduationCap} />
                                                </small>}
                                                {location.trainStation && <small className="text-truncate">
                                                    <FontAwesomeIcon className="ml-1 animated zoomIn icon-train" icon={faTrain} />
                                                </small>}
                                                {location.pubIsClosed && <small className="text-truncate">
                                                    <FontAwesomeIcon className="ml-1 animated zoomIn icon-closed" icon={faTimes} />
                                                </small>}
                                                {(location.pubIsTemporaryClosed && !location.pubIsClosed) && <small className="text-truncate">
                                                    <FontAwesomeIcon className="ml-1 animated zoomIn icon-temporary" icon={faHammer} />
                                                </small>}
                                                <Link to={`/admin/${location.id}`}>
                                                    <FontAwesomeIcon className="ml-2 animated zoomIn icon-pen cursor-pointer" icon={faPencilAlt} />
                                                </Link>
                                            </div>
                                            <div className="ml-1">
                                                <small className="text-truncate">
                                                    <div className="d-inline-block" style={{ marginRight: "0.3em" }}>{getCountryFlag(location.country || '')}</div>
                                                    <div className="d-inline-block">{location.address}</div>
                                                </small>
                                            </div>
                                        </Col>
                                        {filter === 'NO_COUNTRY' && (
                                            <Col xs="auto" className="d-flex align-items-center">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    className="mr-1"
                                                    onClick={() => setCountry(location.id, 'ENG')}
                                                >
                                                    ENG
                                                </Button>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    className="mr-1"
                                                    onClick={() => setCountry(location.id, 'SCO')}
                                                >
                                                    SCO
                                                </Button>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    className="mr-1"
                                                    onClick={() => setCountry(location.id, 'WAL')}
                                                >
                                                    WAL
                                                </Button>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    className="mr-1"
                                                    onClick={() => setCountry(location.id, 'NIR')}
                                                >
                                                    NIR
                                                </Button>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    className="mr-1"
                                                    onClick={() => setCountry(location.id, 'IRE')}
                                                >
                                                    IRE
                                                </Button>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    className="mr-1"
                                                    onClick={() => setCountry(location.id, 'ESP')}
                                                >
                                                    ESP
                                                </Button>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => setCountry(location.id, 'IOM')}
                                                >
                                                    IOM
                                                </Button>
                                            </Col>
                                        )}
                                    </Row>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Card.Body>
            </Card>
        </>
    );
}

export default SuperAdminPubs;
